import { createAsyncThunk } from '@reduxjs/toolkit'
import { get, post, put, remove } from '../services/crud'
import { ServiceConfig } from '../services/config'
import {
  AddFormInput,
  AddFormPacketRequest,
  AddFormResponse,
  AgreementConfigs,
  ApiError,
  GetEmailHistoryResponse,
  GetFormsInput,
  GetFormsResponse,
  GetTemplateDetailInput,
  IFormDetail,
  ITemplate,
  ITemplateDetail,
} from './typings'
import {
  ENTITY_TYPES,
  objectToQueryString,
  toQueryString,
} from '@sales-center/common'

const baseUrl = `${ServiceConfig.baseAPIUrl}/Proposals`

// GET LEAD/OPP-SPECIFIC TEMPLATES
export const getTemplates = createAsyncThunk<
  ITemplate[],
  GetTemplatesParams,
  { rejectValue: ApiError }
>('agreement/getTemplatesByEntityId', async (model, thunkApi) => {
  const response = await get(`${baseUrl}/templates?${toQueryString(model)}`)

  if (response.ok) {
    const payload: ITemplate[] = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

type GetTemplatesParams = {
  proposalEntityType: ENTITY_TYPES
  proposalEntityId: number
}

// GET GLOBAL TEMPLATES
export const getGlobalTemplates = createAsyncThunk<
  ITemplate[],
  void,
  { rejectValue: ApiError }
>('agreement/getGlobalTemplates', async (_, thunkApi) => {
  const response = await get(`${baseUrl}/templates/global`)

  if (response.ok) {
    const payload: ITemplate[] = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// GET GLOBAL & LEAD/OPP-SPECIFIC TEMPLATES
export const getAllTemplates = createAsyncThunk<
  ITemplate[],
  GetTemplatesParams,
  { rejectValue: ApiError }
>('agreement/getAllTemplates', async (model, thunkApi) => {
  const templatesResponse = await get(
    `${baseUrl}/templates?${toQueryString(model)}`,
  )
  const globalTemplatesResponse = await get(`${baseUrl}/templates/global`)

  if (templatesResponse.ok && globalTemplatesResponse.ok) {
    const templates: ITemplate[] = await templatesResponse.json()
    const globalTemplates: ITemplate[] = await globalTemplatesResponse.json()

    const payload: ITemplate[] = [...templates, ...globalTemplates]
    return payload
  }

  return thunkApi.rejectWithValue((await templatesResponse.json()) as ApiError)
})

// GET EMAIL HISTORY BY AGREEMENT ID
export const getEmailHistory = createAsyncThunk<
  GetEmailHistoryResponse,
  number,
  { rejectValue: ApiError }
>('agreement/getEmailHistory', async (formId, thunkApi) => {
  const response = await get(`${baseUrl}/form/${formId}/emailHistory`)

  if (response.ok) {
    const payload: GetEmailHistoryResponse = await response.json()

    if (!!payload.records) {
      payload.records.forEach((record) => {
        record.date_cancelled = record.date_cancelled
          ? new Date(record.date_cancelled)
          : null
        record.date_created = record.date_created
          ? new Date(record.date_created)
          : null
        record.date_delivered = record.date_delivered
          ? new Date(record.date_delivered)
          : null
        record.date_failed = record.date_failed
          ? new Date(record.date_failed)
          : null
        record.date_lastClicked = record.date_lastClicked
          ? new Date(record.date_lastClicked)
          : null
        record.date_lastOpened = record.date_lastOpened
          ? new Date(record.date_lastOpened)
          : null
        record.date_queued = record.date_queued
          ? new Date(record.date_queued)
          : null
        record.date_sent = record.date_sent ? new Date(record.date_sent) : null
      })
    }

    return payload
  }

  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// GET TEMPLATE BY ID
export const getTemplateById = createAsyncThunk<
  ITemplateDetail,
  GetTemplateDetailInput,
  { rejectValue: ApiError }
>('agreement/getTemplateById', async (model, thunkApi) => {
  const response = await get(
    `${baseUrl}/templates/${model.id}?${objectToQueryString(model || '')}`,
  )

  if (response.ok) {
    const payload: ITemplateDetail = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// GET FORM BY ID
export const getFormById = createAsyncThunk<
  IFormDetail,
  number,
  { rejectValue: ApiError }
>('agreement/getFormById', async (id, thunkApi) => {
  const response = await get(`${baseUrl}/form/${id}`)

  if (response.ok) {
    const payload: IFormDetail = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// GET FORMS
export const getForms = createAsyncThunk<
  GetFormsResponse,
  GetFormsInput,
  { rejectValue: ApiError }
>('agreement/getForms', async (params, thunkApi) => {
  const response = await get(`${baseUrl}/forms?${toQueryString(params)}`)

  if (response.ok) {
    const payload: GetFormsResponse = await response.json()
    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// ADD FORM
export const addForm = createAsyncThunk<
  AddFormResponse,
  AddFormInput,
  { rejectValue: ApiError }
>('agreement/addForm', async (model, thunkApi) => {
  const response = await post(`${baseUrl}/form`, model)

  if (response.ok) {
    const payload: AddFormResponse = await response.json()

    if (payload.status == 'success') {
      return payload
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// CANCEL FORM
export const cancelForm = createAsyncThunk<
  AddFormResponse,
  number,
  { rejectValue: ApiError }
>('agreement/cancelForm', async (formId, thunkApi) => {
  const response = await post(`${baseUrl}/form/${formId}/cancel`)

  if (response.ok) {
    const payload: AddFormResponse = await response.json()

    if (payload.status == 'success') {
      return payload
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// DELETE FORM
export const deleteForm = createAsyncThunk<
  AddFormResponse,
  number,
  { rejectValue: ApiError }
>('agreement/deleteForm', async (formId, thunkApi) => {
  const response = await remove(`${baseUrl}/form/${formId}`)

  if (response.ok) {
    const payload: AddFormResponse = await response.json()
    payload.formId = formId

    if (payload.status == 'success') {
      return payload
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// REVISE FORM
export const reviseForm = createAsyncThunk<
  AddFormResponse,
  number,
  { rejectValue: ApiError }
>('agreement/reviseForm', async (formId, thunkApi) => {
  const response = await post(`${baseUrl}/form/${formId}/revise`)

  if (response.ok) {
    const payload: AddFormResponse = await response.json()

    if (payload.status == 'success') {
      return payload
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// CREATE FORM PACKET
export const createFormPacket = createAsyncThunk<
  AddFormResponse,
  AddFormPacketRequest,
  { rejectValue: ApiError }
>('agreement/formPacket', async (data, thunkApi) => {
  const response = await post(`${baseUrl}/form`, data)

  if (response.ok) {
    return await response.json()
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// UPDATE FORM PACKET
export const updateFormPacket = createAsyncThunk<
  AddFormResponse,
  AddFormPacketRequest,
  { rejectValue: ApiError }
>('agreement/formPacket', async (data, thunkApi) => {
  const response = await put(`${baseUrl}/form/updateTemplatesAndServices`, data)

  if (response.ok) {
    return await response.json()
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// GET AGREEMENT CONFIG
export const getAgreementConfigs = createAsyncThunk<
  AgreementConfigs,
  number,
  { rejectValue: ApiError }
>('agreement/configs', async (formId, thunkApi) => {
  const response = await get(`${baseUrl}/agreementConfig/${formId}`)

  if (response.ok) {
    const payload: AgreementConfigs = await response.json()
    return payload
  }

  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})
